import { Injectable } from '@angular/core';
import { withTimeout } from './offline-network-failure';
import { deleteAllVideoFiles } from './offline-video-files';
import {
  OFFLINE_DB_NAME,
  OFFLINE_DB_VERSION,
  STORE_KEYS,
  STORE_ENTITLEMENT,
  STORE_CONTENT,
  STORE_MEDIA,
  STORE_INDEX,
  DEVICE_KEY_ID,
  OFFLINE_DATA_FORMAT,
  DATA_FORMAT_ID,
  DeviceKeyRecord,
  EntitlementRecord,
  ContentRecord,
  MediaRecord,
  IndexRecord,
} from './offline-db.types';

// Hand-written, promise-wrapped IndexedDB access layer. No dependency added — idb/dexie
// don't exist anywhere in this app today, and the plan explicitly rules out a new shared
// workspace library. Mirrors the memoised-promise pattern already used by
// SchoolmanagementService.getSchoolFromSubdomain for the DB-open handle.
@Injectable({
  providedIn: 'root',
})
export class OfflineDbService {
  private openDbPromise: Promise<IDBDatabase> | null = null;

  // EVERY IndexedDB request below can hang forever if it is not bounded, and that is not
  // theoretical — it is what broke the installed app. indexedDB.open() fires `blocked`, NOT
  // `error`, when another connection on the same origin is holding the database and has not
  // responded to a versionchange. `blocked` has no default behaviour: with no handler the
  // request simply never settles, so the promise never settles, so anything awaiting it waits
  // for the rest of the session. In an installed app the other connection is typically a
  // service worker that stays alive between launches, which is why browser tabs looked fine
  // and the PWA did not.
  //
  // So: a handler for `blocked`, and a ceiling over the whole thing in case a request settles
  // in no way at all. Short, because nothing here is worth making a student stare at a blank
  // screen for — every caller already copes with the database being unavailable.
  private static readonly OPEN_TIMEOUT_MS = 5000;
  private static readonly WIPE_TIMEOUT_MS = 5000;

  private openDb(): Promise<IDBDatabase> {
    if (!this.openDbPromise) {
      this.openDbPromise = this.openDbBounded().catch((err) => {
        this.openDbPromise = null; // let the next caller try again rather than caching a failure
        throw err;
      });
    }
    return this.openDbPromise;
  }

  // The timeout lives INSIDE this promise rather than wrapping it from outside, and that is
  // the whole point. A Promise.race() around indexedDB.open() rejects on time but cannot
  // reach the request it gave up on — so if the open later succeeds, that connection is open,
  // unreferenced, and unclosable. An abandoned open connection is exactly what blocks the
  // next tab's open or delete, which is the bug this method exists to prevent. Racing from
  // outside would have made this code a source of it.
  private openDbBounded(): Promise<IDBDatabase> {
    return new Promise<IDBDatabase>((resolve, reject) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new Error(`indexedDB.open did not respond within ${OfflineDbService.OPEN_TIMEOUT_MS}ms`));
      }, OfflineDbService.OPEN_TIMEOUT_MS);

      const finish = (fn: () => void) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        fn();
      };

      const req = indexedDB.open(OFFLINE_DB_NAME, OFFLINE_DB_VERSION);

      req.onblocked = () => finish(() => reject(new Error('indexedDB.open blocked by another connection')));

      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_KEYS)) {
          db.createObjectStore(STORE_KEYS, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(STORE_ENTITLEMENT)) {
          db.createObjectStore(STORE_ENTITLEMENT, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(STORE_CONTENT)) {
          db.createObjectStore(STORE_CONTENT, { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains(STORE_MEDIA)) {
          db.createObjectStore(STORE_MEDIA, { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains(STORE_INDEX)) {
          const idx = db.createObjectStore(STORE_INDEX, { keyPath: 'key' });
          idx.createIndex('by_school_user', ['school', 'user'], { unique: false });
        }
      };

      req.onsuccess = () => {
        const db = req.result;
        // Stand aside instead of becoming the connection that blocks somebody else — the
        // other half of the bug above. Without this, THIS tab is what stops another one
        // from upgrading or deleting the database.
        db.onversionchange = () => {
          db.close();
          this.openDbPromise = null;
        };
        // Arrived after we gave up: close it rather than leaving it open forever.
        if (settled) {
          db.close();
          return;
        }
        finish(() => resolve(db));
      };

      req.onerror = () => finish(() => reject(req.error));
    });
  }

  private async put<T>(storeName: string, value: T): Promise<void> {
    const db = await this.openDb();
    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction([storeName], 'readwrite');
      tx.objectStore(storeName).put(value);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  private async get<T>(storeName: string, key: string): Promise<T | undefined> {
    const db = await this.openDb();
    return new Promise<T | undefined>((resolve, reject) => {
      const tx = db.transaction([storeName], 'readonly');
      const req = tx.objectStore(storeName).get(key);
      req.onsuccess = () => resolve(req.result as T | undefined);
      req.onerror = () => reject(req.error);
    });
  }

  private async delete(storeName: string, key: string): Promise<void> {
    const db = await this.openDb();
    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction([storeName], 'readwrite');
      tx.objectStore(storeName).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  private async getAllByIndex<T>(storeName: string, indexName: string, query: IDBValidKey | IDBKeyRange): Promise<T[]> {
    const db = await this.openDb();
    return new Promise<T[]>((resolve, reject) => {
      const tx = db.transaction([storeName], 'readonly');
      const req = tx.objectStore(storeName).index(indexName).getAll(query);
      req.onsuccess = () => resolve((req.result as T[]) ?? []);
      req.onerror = () => reject(req.error);
    });
  }

  // ---- keys ----

  async getDeviceKeyRecord(): Promise<DeviceKeyRecord | undefined> {
    return this.get<DeviceKeyRecord>(STORE_KEYS, DEVICE_KEY_ID);
  }

  // Read an arbitrary row from the keys store by id. Only the diagnostics panel uses this, to
  // report the stored data-format stamp without that file needing to know the store layout.
  async getRawKeyRecord<T>(id: string): Promise<T | undefined> {
    return this.get<T>(STORE_KEYS, id);
  }

  // Stores `record` as THE device key only if there is none yet, and returns whichever key is
  // stored afterwards - the caller must use that one, not the one it offered.
  //
  // Read-then-put from separate transactions was a race that destroyed saved content: every
  // copy of the app running at once (the lesson page plus one per video iframe in its notes)
  // found no key, generated its own and put it, last write winning. Each copy then encrypted
  // with its own in-memory key - fine until the app was closed, when the next launch loaded
  // the winner and everything written by the other copies became undecryptable. A single
  // readwrite transaction serialises against every other one on this store, so the check and
  // the write can no longer interleave.
  async claimDeviceKeyRecord(record: DeviceKeyRecord): Promise<DeviceKeyRecord> {
    const db = await this.openDb();
    return new Promise<DeviceKeyRecord>((resolve, reject) => {
      const tx = db.transaction([STORE_KEYS], 'readwrite');
      const store = tx.objectStore(STORE_KEYS);
      let winner: DeviceKeyRecord = record;
      const existing = store.get(DEVICE_KEY_ID);
      existing.onsuccess = () => {
        if (existing.result) {
          winner = existing.result as DeviceKeyRecord;
        } else {
          store.add(record);
        }
      };
      tx.oncomplete = () => resolve(winner);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  }

  // ---- entitlement ----
  // Written by OfflineEntitlementService. The read path here also backs the downloads hub's
  // own fail-closed guard (offline-content-reader.service.ts) — an absent or expired record
  // means "refuse to serve," never "assume allowed."

  async getEntitlement(id: string): Promise<EntitlementRecord | undefined> {
    return this.get<EntitlementRecord>(STORE_ENTITLEMENT, id);
  }

  async putEntitlement(record: EntitlementRecord): Promise<void> {
    return this.put(STORE_ENTITLEMENT, record);
  }

  async deleteEntitlement(id: string): Promise<void> {
    return this.delete(STORE_ENTITLEMENT, id);
  }

  // ---- content ----

  async getContent(key: string): Promise<ContentRecord | undefined> {
    return this.get<ContentRecord>(STORE_CONTENT, key);
  }

  async putContent(record: ContentRecord): Promise<void> {
    return this.put(STORE_CONTENT, record);
  }

  async deleteContent(key: string): Promise<void> {
    return this.delete(STORE_CONTENT, key);
  }

  // ---- media (manifest + chunk rows share the store, distinguished by recordType) ----

  async putMedia(record: MediaRecord): Promise<void> {
    return this.put(STORE_MEDIA, record);
  }

  async getMedia(key: string): Promise<MediaRecord | undefined> {
    return this.get<MediaRecord>(STORE_MEDIA, key);
  }

  async deleteMedia(key: string): Promise<void> {
    return this.delete(STORE_MEDIA, key);
  }

  // Every saved video for this user. Video downloads write only to `media` and never create
  // an IndexRecord, so this is the only way to enumerate them - listIndexEntries cannot see
  // them, which is why reconnect revalidation never covered video.
  async listMediaManifests(school: string, user: number): Promise<MediaRecord[]> {
    return (await this.readManifests()).filter((r) => r.school === school && r.user === user);
  }

  // No index on `media` — a scan of a store that holds one small manifest per saved video. Used
  // to answer "is anything downloaded for this video" before media.component.ts decides whether
  // to offer offline playback.
  async findMediaManifest(school: string, user: number, resourceId: string): Promise<MediaRecord | undefined> {
    return (await this.readManifests()).find((r) => r.school === school && r.user === user && r.resourceId === resourceId);
  }

  // Current-design manifests only. Walks KEYS, then reads just the manifest rows: until the
  // legacy cleanup has run, this store can also hold hundreds of 1 MiB encrypted chunks, and a
  // value cursor would pull every one of them into memory on each video page (see
  // deleteLegacyMediaRows).
  private async readManifests(): Promise<MediaRecord[]> {
    const db = await this.openDb();
    if (!db.objectStoreNames.contains(STORE_MEDIA)) return [];
    const keys = await new Promise<string[]>((resolve, reject) => {
      const found: string[] = [];
      const req = db.transaction([STORE_MEDIA], 'readonly').objectStore(STORE_MEDIA).openKeyCursor();
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) return resolve(found);
        const key = String(cursor.primaryKey);
        if (key.endsWith(':manifest')) found.push(key);
        cursor.continue();
      };
      req.onerror = () => reject(req.error);
    });
    const records: MediaRecord[] = [];
    for (const key of keys) {
      const record = await this.get<MediaRecord>(STORE_MEDIA, key);
      if (record && isFileManifest(record)) records.push(record);
    }
    return records;
  }

  // Removes every row the previous video design wrote: encrypted chunks, and the manifests that
  // pointed at them. Neither can play under the file-based design. Touches nothing outside the
  // `media` store, so saved notes are unaffected. Returns how many rows went.
  //
  // MUST NEVER LOAD A CHUNK'S VALUE. Each chunk is 1 MiB of ciphertext and a saved video is
  // hundreds of them. The first version of this walked a value cursor, so it pulled every chunk
  // into memory — in the app and again in every note's /media iframe, all at startup — which is
  // enough to get the page killed on an iPhone. A killed page never commits the delete, so it
  // retried on every launch and the installed app could not get past startup.
  //
  // So: a KEY cursor to find candidates (keys only — chunk keys contain ':chunk:'), a get() only
  // for manifest rows (small), and deletes in small separate transactions so no single one is
  // large or long-lived.
  async deleteLegacyMediaRows(): Promise<number> {
    const db = await this.openDb();
    if (!db.objectStoreNames.contains(STORE_MEDIA)) return 0;

    const chunkKeys: IDBValidKey[] = [];
    const manifestKeys: IDBValidKey[] = [];
    await new Promise<void>((resolve, reject) => {
      const req = db.transaction([STORE_MEDIA], 'readonly').objectStore(STORE_MEDIA).openKeyCursor();
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) return resolve();
        const key = String(cursor.primaryKey);
        if (key.includes(':chunk:')) chunkKeys.push(cursor.primaryKey);
        else manifestKeys.push(cursor.primaryKey);
        cursor.continue();
      };
      req.onerror = () => reject(req.error);
    });

    const doomed = [...chunkKeys];
    for (const key of manifestKeys) {
      const record = await this.get<MediaRecord>(STORE_MEDIA, key as string);
      if (record && !isFileManifest(record)) doomed.push(key);
    }

    const BATCH = 25;
    for (let i = 0; i < doomed.length; i += BATCH) {
      const batch = doomed.slice(i, i + BATCH);
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction([STORE_MEDIA], 'readwrite');
        const store = tx.objectStore(STORE_MEDIA);
        for (const key of batch) store.delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      });
      // Let the page breathe between batches; this is background housekeeping.
      await new Promise((r) => setTimeout(r, 0));
    }
    return doomed.length;
  }

  // ---- index (saved-state registry) ----

  async getIndexEntry(key: string): Promise<IndexRecord | undefined> {
    return this.get<IndexRecord>(STORE_INDEX, key);
  }

  async listIndexEntries(school: string, user: number): Promise<IndexRecord[]> {
    return this.getAllByIndex<IndexRecord>(STORE_INDEX, 'by_school_user', [school, user]);
  }

  async isSaved(key: string): Promise<boolean> {
    const entry = await this.getIndexEntry(key);
    return !!entry && entry.status === 'saved';
  }

  // Called by OfflineSaveService before firing a unit's requests, so a half-finished save is
  // recorded as 'saving' and never read back as though it were complete.
  async markSaved(record: IndexRecord): Promise<void> {
    return this.put(STORE_INDEX, record);
  }

  async deleteIndexEntry(key: string): Promise<void> {
    return this.delete(STORE_INDEX, key);
  }

  async touchIndex(key: string, currentVersion: string, sizeBytes: number): Promise<void> {
    const existing = await this.getIndexEntry(key);
    if (!existing) return; // nothing to touch — only markSaved() creates a row
    await this.put(STORE_INDEX, {
      ...existing,
      status: 'saved',
      currentVersion,
      sizeBytes,
      savedAt: Date.now(),
    });
  }

  // ---- wipe-on-logout ----

  // Called once at startup, before anything reads offline data. Returns true if it wiped.
  //
  // Stored in the existing `keys` store under its own id rather than in a new store of its
  // own, deliberately: adding a store would need an OFFLINE_DB_VERSION bump, and the whole
  // point of this method is to stop depending on that being done correctly.
  //
  // Absent stamp is treated as format 0, so a database written before this existed is wiped
  // once and then stamped. On a device with nothing saved that wipe is a no-op it never
  // notices.
  async ensureDataFormatCurrent(): Promise<boolean> {
    let stored: number | null = null;
    try {
      const record = await this.get<{ id: string; format?: number }>(STORE_KEYS, DATA_FORMAT_ID);
      stored = typeof record?.format === 'number' ? record.format : null;
    } catch (err) {
      // An unreadable database is itself a reason to start over — that is what this is for.
      console.error('[offline] could not read the data-format stamp; treating as stale', err);
    }

    if (stored === OFFLINE_DATA_FORMAT) return false;

    // NEVER wipe without a network. This method's whole premise is "discard it, they can
    // download it again" — and offline they cannot. A student whose first launch of a new
    // build happens on a train would lose every saved lesson with no way to get it back. The
    // stale data is not dangerous, only unreadable by this build, so leaving it until there
    // is a connection costs nothing. navigator.onLine is unreliable when it says true, but
    // reliable when it says false, which is the only direction that matters here.
    if (navigator.onLine === false) {
      console.warn('[offline] saved data looks stale, but we are offline — leaving it alone until there is a connection');
      return false;
    }

    // Reports whether anything the student actually had was destroyed, which is NOT the same
    // as "a stamp was present". The commonest real case is a device that saved content before
    // this mechanism existed: no stamp at all, but a full set of downloads. Deciding from the
    // stamp alone would report that one as a clean first run.
    const wiped = await this.hasAnySavedData();
    if (wiped) {
      console.warn(`[offline] saved data is format ${stored ?? 'unstamped'}, this build needs ${OFFLINE_DATA_FORMAT} — clearing it`);
    }
    await this.wipeAll();
    // wipeAll deletes the database; this put reopens it, which recreates the stores through
    // onupgradeneeded, and then stamps the format so the next startup is a no-op.
    await this.put(STORE_KEYS, { id: DATA_FORMAT_ID, format: OFFLINE_DATA_FORMAT });
    return wiped;
  }

  // Is there any downloaded content on this device? Counts rather than reads — this only ever
  // needs to know "anything at all". Both stores are checked because a downloaded video lives
  // only in `media` and never gets an index entry (see listMediaManifests above).
  private async hasAnySavedData(): Promise<boolean> {
    try {
      const db = await this.openDb();
      const stores = [STORE_INDEX, STORE_MEDIA].filter((name) => db.objectStoreNames.contains(name));
      if (stores.length === 0) return false;
      const counts = await Promise.all(
        stores.map(
          (name) =>
            new Promise<number>((resolve, reject) => {
              const req = db.transaction([name], 'readonly').objectStore(name).count();
              req.onsuccess = () => resolve(req.result);
              req.onerror = () => reject(req.error);
            }),
        ),
      );
      return counts.some((n) => n > 0);
    } catch (err) {
      console.error('[offline] could not count saved data', err);
      return false; // only affects whether the wipe is announced, never whether it happens
    }
  }

  async wipeAll(): Promise<void> {
    // Saved videos are files outside IndexedDB, so deleting the database alone would orphan
    // them. Bounded like the delete below: logout must never hang on storage.
    await withTimeout(deleteAllVideoFiles(), OfflineDbService.WIPE_TIMEOUT_MS, 'delete saved video files').catch((err) =>
      console.error('[offline] saved video files were not removed', err),
    );
    if (this.openDbPromise) {
      const db = await this.openDbPromise;
      db.close();
    }
    this.openDbPromise = null;
    // Same hazard as openDb: deleteDatabase can be blocked indefinitely by another connection.
    // onblocked already resolved rather than hanging, but a request that settles in no way at
    // all would still have stalled whatever awaited this, so put a ceiling over it too.
    await withTimeout(
      new Promise<void>((resolve, reject) => {
        const req = indexedDB.deleteDatabase(OFFLINE_DB_NAME);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
        req.onblocked = () => resolve(); // another tab still has it open — best effort, don't hang logout
      }),
      OfflineDbService.WIPE_TIMEOUT_MS,
      'indexedDB.deleteDatabase',
    ).catch((err) => console.error('[offline] database wipe did not complete', err));
  }
}

// A saved video under the current, file-based design. Anything else in `media` is a leftover
// of the encrypted-chunk design — see deleteLegacyMediaRows.
function isFileManifest(record: MediaRecord): boolean {
  return record?.recordType === 'manifest' && record.storage === 'opfs' && !!record.fileName;
}
